var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner, FlexBox, FormField, FormGrid, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { formatE164, getPhoneNumberCountryCodeFormatted, isValidPhoneNumber, } from 'lib-shared-phone-numbers';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMount, useUpdateEffect } from 'react-use';
import sanitizeHtml from 'sanitize-html';
import { conversationsApi, } from 'apis/conversations';
import { numbersApi } from 'apis/numbers';
import { templatesApi } from 'apis/templates';
import { smsMaxMessageLength } from 'app/utils/conversation';
import { HookFormCategorizedTagsDropdown, HookFormPhoneNumberSelect, HookFormQueueSelect, HookFormUserPhoneNumberSelect, } from 'components/hookForm';
import { HookFormRichTextField } from 'components/hookForm/hookFormRichTextField';
import { getLocalSettings, updateSettings, } from 'components/settings/api/personal';
import { ToggleBox, ToggleBoxBody, ToggleBoxHead } from 'fable/toggleBox';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { isResponseWithData } from 'utils/apis/axios';
import { createAppModal } from 'utils/modal';
import { useAppSelector } from 'utils/redux/useAppSelector';
import styles from './composeSmsModal.module.scss';
const defaultFromNumberStorageKey = 'composeSmsDefaultFromNumberStorageKey';
const savedDefaultFromNumber = (_a = getLocalSettings()) === null || _a === void 0 ? void 0 : _a[defaultFromNumberStorageKey];
export const ComposeSmsModal = createAppModal({ type: 'app:composeSms', featureFlag: 'sms-compose' }, ({ isOpen, hide }) => {
    const notify = useNotifications();
    const [showInternalNote, setShowInternalNote] = useState(false);
    const form = useForm({ mode: 'all' });
    const [createConversationFromSms, createConversationFromSmsState] = conversationsApi.useCreateConversationFromSmsMutation();
    const [createConversationWrapupMessage] = conversationsApi.useCreateConversationWrapupMessageMutation();
    const [readNumbers, { isLoading: isReadNumbersLoading }] = numbersApi.useLazyReadNumbersQuery();
    const profile = useAppSelector((state) => state.settings.profile.profile);
    const organization = useAppSelector((state) => state.settings.organization.organization);
    const [requestRenderTemplate] = templatesApi.useLazyRenderTemplateQuery();
    // Search the API for the number saved in local storage and if found, set it as the initial
    // value in the form when the component mounts.
    useMount(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!savedDefaultFromNumber)
            return;
        const response = yield readNumbers({
            searchTerm: savedDefaultFromNumber,
        }, true).unwrap();
        if (isResponseWithData(response)) {
            const [defaultFromNumber] = response.data.numbers;
            if (defaultFromNumber.e164_format === savedDefaultFromNumber) {
                form.setValue('from', [defaultFromNumber]);
            }
        }
    }));
    const fromValue = useWatch({ control: form.control, name: 'from' });
    const toValue = useWatch({ control: form.control, name: 'to' });
    const handleSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const { internalNote } = values, createSmsValues = __rest(values, ["internalNote"]);
        const createSmsResponse = yield createConversationFromSms(Object.assign(Object.assign({}, createSmsValues), { to: (_b = formatE164((_a = createSmsValues.to) === null || _a === void 0 ? void 0 : _a.phoneNumber)) !== null && _b !== void 0 ? _b : '', from: (_c = formatE164(createSmsValues.from[0].e164_format)) !== null && _c !== void 0 ? _c : '', queueId: values.queueId, tags: values.tags ? values.tags : [], message: values.message }));
        if (isResponseWithData(createSmsResponse)) {
            notify({
                title: 'New message sent',
                text: `The sms conversation has been created.`,
                icon: 'i-done',
                time: 5000,
                leftAction: {
                    text: 'Go to conversation',
                    action: {
                        navigate: `/conversation/${createSmsResponse.data.data.csid}`,
                    },
                },
            }, NotificationType.Success);
            if (showInternalNote && internalNote) {
                const createInternalNoteResponse = yield createConversationWrapupMessage(Object.assign(Object.assign({}, values), { csid: createSmsResponse.data.data.csid, text: internalNote }));
                if (!isResponseWithData(createInternalNoteResponse)) {
                    notify({
                        title: 'Internal note not created.',
                    }, NotificationType.Error);
                }
            }
            hide === null || hide === void 0 ? void 0 : hide();
        }
        else {
            notify({
                title: 'Error creating sms conversation.',
            }, NotificationType.Error);
        }
    });
    // Handles triggering validating of the `to` field when `from` field changes, as well as saving
    // the selected `from` number to local storage to be used as the default next time.
    useUpdateEffect(() => {
        if (toValue) {
            form.trigger('to');
        }
        const fromNumber = fromValue[0].e164_format;
        if (fromNumber !== savedDefaultFromNumber) {
            updateSettings({
                key: defaultFromNumberStorageKey,
                value: fromNumber,
            });
        }
    }, [fromValue]);
    const renderTemplate = ({ content }) => __awaiter(void 0, void 0, void 0, function* () {
        var _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        const context = {
            organization_name: sanitizeHtml(organization.name),
            requester_firstname: ((_e = (_d = toValue === null || toValue === void 0 ? void 0 : toValue.searchUserHit) === null || _d === void 0 ? void 0 : _d.display_name) === null || _e === void 0 ? void 0 : _e.split(' ')[0])
                ? sanitizeHtml(toValue.searchUserHit.display_name.split(' ')[0])
                : '',
            requester_fullname: sanitizeHtml((_g = (_f = toValue === null || toValue === void 0 ? void 0 : toValue.searchUserHit) === null || _f === void 0 ? void 0 : _f.display_name) !== null && _g !== void 0 ? _g : ''),
            requester_email: sanitizeHtml((_j = (_h = toValue === null || toValue === void 0 ? void 0 : toValue.searchUserHit) === null || _h === void 0 ? void 0 : _h.email) !== null && _j !== void 0 ? _j : ''),
            agent_firstname: sanitizeHtml((_k = profile.given_name) !== null && _k !== void 0 ? _k : ''),
            agent_lastname: sanitizeHtml((_l = profile.family_name) !== null && _l !== void 0 ? _l : ''),
            agent_fullname: sanitizeHtml(`${(_m = profile.given_name) !== null && _m !== void 0 ? _m : ''} ${(_o = profile.family_name) !== null && _o !== void 0 ? _o : ''}`),
            agent_displayname: sanitizeHtml(profile.name),
        };
        return requestRenderTemplate({
            content,
            context,
        }).unwrap();
    });
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: hide, className: styles.modal }, { children: [_jsx(ModalHead, { title: "Compose new SMS", onClose: hide }), _jsx(ModalBody, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "400" }, { children: [_jsx("div", { children: _jsx(Banner, { variant: "info", description: _jsx(Text.Paragraph, Object.assign({ as: "span", variant: "4" }, { children: "The origin and destination numbers need to be in the same country code." })) }) }), _jsx("div", { children: _jsxs(FormGrid, { children: [_jsx(FormField, { children: _jsx(HookFormPhoneNumberSelect, { name: "from", label: "From", control: form.control, rules: {
                                                required: 'Phone number is required',
                                            }, componentProps: {
                                                capability: 'sms',
                                                showCountryFlag: true,
                                            }, disabled: isReadNumbersLoading }) }), _jsx(FormField, { children: _jsx(HookFormUserPhoneNumberSelect, { name: "to", label: "To", control: form.control, rules: {
                                                validate: {
                                                    isValidFormat: (value) => (value && isValidPhoneNumber(value.phoneNumber)) ||
                                                        `Invalid phone number.`,
                                                    phoneNumber: (value) => {
                                                        var _a, _b;
                                                        return !fromValue ||
                                                            ((_a = value === null || value === void 0 ? void 0 : value.phoneNumber) === null || _a === void 0 ? void 0 : _a.startsWith((_b = getPhoneNumberCountryCodeFormatted(fromValue[0].e164_format)) !== null && _b !== void 0 ? _b : '+0')) ||
                                                            'The origin and destination numbers must be in the same country';
                                                    },
                                                },
                                                required: 'Phone number is required',
                                            }, componentProps: {
                                                infiniteScroll: false,
                                                showRegionFlagPrefix: true,
                                            } }) }), _jsx(FormField, { children: _jsx(HookFormRichTextField, { name: "message", label: "Message", control: form.control, rules: {
                                                required: 'Message is required',
                                                maxLength: {
                                                    value: smsMaxMessageLength,
                                                    message: `Reached limit of ${smsMaxMessageLength} characters`,
                                                },
                                            }, componentProps: {
                                                placeholder: 'Type text message...',
                                                showCharacterLimit: true,
                                                hideSendOnEnterToggle: true,
                                                hasBorder: true,
                                                channel: 'sms',
                                                renderTemplate,
                                                disableSubmitMessageWithEnter: true,
                                                onTriggerSend: form.handleSubmit(handleSubmit),
                                            } }) }), _jsx(FormField, { children: _jsx(HookFormQueueSelect, { name: "queueId", label: "Queue", control: form.control, componentProps: {
                                                infiniteScroll: false,
                                            } }) }), _jsx(FormField, { children: _jsx(HookFormCategorizedTagsDropdown, { control: form.control, label: "Tags", name: "tags", componentProps: {
                                                items: [],
                                                selectedItems: [],
                                                disabledItems: [],
                                            } }) }), _jsx(FormField, { children: _jsx(ToggleBox, Object.assign({ className: styles.toggleBox, isOpen: showInternalNote, headComponent: ToggleBoxHead, title: 'Internal Note', helpIcon: undefined, input: {
                                                id: 'internal-note-toggle-box',
                                                name: 'internal-note-toggle-box',
                                                value: 'internal-note-toggle-box',
                                            }, onChange: (open) => setShowInternalNote(open) }, { children: _jsx(ToggleBoxBody, { children: showInternalNote && (_jsx(FormField, { children: _jsx(HookFormRichTextField, { name: "internalNote", control: form.control, componentProps: {
                                                            className: styles.internalNoteEditorColor,
                                                            placeholder: 'Type an internal note...',
                                                            showCharacterLimit: true,
                                                            renderTemplate,
                                                            channel: 'sms',
                                                            hasBorder: true,
                                                            hideSendOnEnterToggle: true,
                                                            disableSubmitMessageWithEnter: true,
                                                            onTriggerSend: form.handleSubmit(handleSubmit),
                                                        } }) })) }) })) })] }) })] })) }), _jsx(ModalFoot, { cancelOnClick: hide, confirmName: "Send", confirmOnClick: form.handleSubmit(handleSubmit), confirmProps: {
                    isLoading: createConversationFromSmsState.isLoading,
                    loadingIndicator: 'Sending...',
                    disabled: !form.formState.isValid || form.formState.isSubmitting,
                } })] })));
});
